import { motion } from "framer-motion";
import { GitBranch, MessageSquare, ShieldCheck, TrendingUp } from "lucide-react";
import {
  CHALLENGE_GROUPS,
  INTRO_MEDIA,
  OBJECTIVE_GROUPS,
  VISION_PILLARS,
} from "../../constants/collect";
import { CornerMarks, MediaSlot, SectionShell, useFade } from "./ui";

const OBJECTIVE_ICONS = {
  trend: TrendingUp,
  flow: GitBranch,
  message: MessageSquare,
  shield: ShieldCheck,
};

function CollectStory() {
  const { fade } = useFade();

  return (
    <>
      <SectionShell id="intro" labelledBy="intro-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Introduction
            </p>
            <h2
              id="intro-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Intelligent recovery
              <span className="mt-1 block text-collect-red">beyond traditional collections</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Across Africa, digital lending continues to expand. Inclusion has improved — and so
              have defaults, overdue repayments and non-performing assets.
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-12">
            <motion.div
              className="relative min-h-[22rem] lg:min-h-[34rem]"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <CornerMarks />
              <MediaSlot
                src={INTRO_MEDIA.src}
                alt={INTRO_MEDIA.alt}
                label="Introduction"
                spec={INTRO_MEDIA.spec}
                className="h-full min-h-[22rem] w-full lg:absolute lg:inset-0 lg:min-h-full"
                tone="light"
              />
            </motion.div>

            <motion.ol
              className="relative flex flex-col justify-between border border-navy/10 bg-[#faf6f4] px-6 py-7 sm:px-8 sm:py-9"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
              aria-label="From pressure to platform"
            >
              <span
                className="pointer-events-none absolute bottom-9 left-[2.15rem] top-9 w-px bg-collect-red/25 sm:left-[2.65rem]"
                aria-hidden="true"
              />
              {[
                {
                  n: "01",
                  title: "The pressure",
                  copy: "Loan defaults, overdue repayments and non-performing assets are rising as digital financial services scale across Africa.",
                },
                {
                  n: "02",
                  title: "The old desk",
                  copy: "Many organizations still run recovery on spreadsheets, disconnected systems, phone calls and fragmented communication — inefficient, hard to monitor and expensive.",
                },
                {
                  n: "03",
                  title: "The platform",
                  copy: "SimCollect automates the full lifecycle: portfolio onboarding, case assignment, customer engagement, payment reconciliation and executive reporting — through Mobile Money, USSD, payment links and the contact centre.",
                },
              ].map((item, index) => (
                <li key={item.n} className={`relative pl-10 sm:pl-12 ${index === 1 ? "py-8" : ""}`}>
                  <span className="absolute left-0 top-1 flex h-7 w-7 items-center justify-center bg-white font-mono text-[10px] tracking-[0.12em] text-collect-red ring-1 ring-collect-red/25">
                    {item.n}
                  </span>
                  <h3 className="text-lg font-semibold tracking-tight text-navy">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/65 sm:text-base">{item.copy}</p>
                  {index === 1 ? (
                    <p className="mt-4 text-base font-semibold tracking-tight text-collect-red">
                      SimCollect changes this.
                    </p>
                  ) : null}
                </li>
              ))}
            </motion.ol>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="why-simcollect" labelledBy="why-heading" tone="paper">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-14">
            <motion.header
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                Why SimCollect matters
              </p>
              <h2
                id="why-heading"
                className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
              >
                The challenge facing
                <span className="mt-1 block">modern debt recovery</span>
              </h2>
            </motion.header>
            <motion.p
              className="max-w-md text-base leading-relaxed text-navy/60 sm:text-lg"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              Traditional collection methods often focus only on reminders and follow-up calls,
              without complete visibility into the recovery lifecycle.
            </motion.p>
          </div>

          <ul className="mt-12 grid gap-4 lg:grid-cols-3">
            {CHALLENGE_GROUPS.map((group) => (
              <motion.li
                key={group.n}
                className="flex flex-col border border-navy/10 bg-white p-6 sm:p-7"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                <p className="font-mono text-[11px] tracking-[0.18em] text-collect-red">{group.n}</p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">{group.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-navy/55">{group.copy}</p>
                <ol className="mt-6 flex-1 space-y-3 border-t border-navy/10 pt-5">
                  {group.items.map((item, index) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-semibold leading-snug text-navy">
                      <span className="font-mono text-[11px] tracking-[0.14em] text-collect-gold">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {item}
                    </li>
                  ))}
                </ol>
              </motion.li>
            ))}
          </ul>

          <motion.div
            className="mt-4 border border-navy/10 bg-navy px-6 py-8 text-white sm:px-8 sm:py-10"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-collect-gold">
              The SimCollect response
            </p>
            <p className="mt-4 max-w-3xl text-lg font-semibold leading-snug tracking-tight sm:text-xl">
              Intelligent automation, real-time monitoring and customer-centric engagement —
              so institutions recover faster while remaining fair, transparent and compliant.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm font-semibold text-white/75">
              {["Fairness", "Transparency", "Compliance", "Complete audit trails"].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-collect-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </SectionShell>

      <SectionShell id="vision" labelledBy="vision-heading" tone="navy">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-collect-gold to-transparent"
            aria-hidden="true"
          />
          <p
            className="pointer-events-none absolute -left-2 top-10 select-none text-[7rem] font-black leading-none tracking-tight text-white/[0.045] sm:text-[10rem] lg:top-6 lg:text-[12rem]"
            aria-hidden="true"
          >
            VISION
          </p>

          <div className="relative border border-white/10 bg-white/[0.03] p-6 sm:p-10 lg:p-12">
            <CornerMarks dark />
            <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-14">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-collect-gold">
                  Our vision
                </p>
                <h2
                  id="vision-heading"
                  className="mt-6 font-semibold leading-[1.18] tracking-tight text-white [font-size:clamp(1.55rem,3.2vw,2.55rem)]"
                >
                  To become Africa&apos;s leading intelligent digital debt recovery and collection
                  management platform by transforming how organizations recover outstanding debts
                  through automation, digital engagement, analytics and responsible collection
                  practices.
                </h2>
              </motion.div>

              <ul className="grid gap-4 sm:grid-cols-2">
                {VISION_PILLARS.map((item) => (
                  <li key={item.n} className="border border-white/12 bg-white/[0.04] p-4">
                    <p className="font-mono text-[11px] tracking-[0.18em] text-collect-gold">{item.n}</p>
                    <h3 className="mt-2 text-base font-semibold tracking-tight text-white">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">{item.copy}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="objectives" labelledBy="objectives-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Service objectives
            </p>
            <h2
              id="objectives-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Designed to help organizations
              <span className="mt-1 block text-collect-red">recover more, operate better</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Outcomes across the book, the desk, the customer and the board — one recovery
              platform.
            </p>
          </motion.header>

          <ul className="mt-12 grid gap-4 lg:grid-cols-2">
            {OBJECTIVE_GROUPS.map((group) => {
              const Icon = OBJECTIVE_ICONS[group.icon] || TrendingUp;
              const navy = group.tone === "navy";
              const paper = group.tone === "paper";
              return (
                <motion.li
                  key={group.n}
                  className={`relative flex flex-col p-6 sm:p-8 ${
                    navy
                      ? "bg-navy text-white"
                      : paper
                        ? "border border-navy/10 bg-[#faf6f4] text-navy"
                        : "border border-navy/10 bg-white text-navy"
                  }`}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                  variants={fade}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p
                        className={`font-mono text-[11px] tracking-[0.18em] ${
                          navy ? "text-collect-gold" : "text-collect-red"
                        }`}
                      >
                        {group.n}
                      </p>
                      <h3 className="mt-3 text-2xl font-semibold tracking-tight">{group.title}</h3>
                      <p className={`mt-2 max-w-sm text-sm leading-relaxed ${navy ? "text-white/65" : "text-navy/55"}`}>
                        {group.copy}
                      </p>
                    </div>
                    <span
                      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center ${
                        navy ? "bg-white/8 text-collect-gold" : "bg-white text-collect-red ring-1 ring-navy/10"
                      }`}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                    </span>
                  </div>
                  <ul className={`mt-6 space-y-3 border-t pt-5 ${navy ? "border-white/12" : "border-navy/10"}`}>
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm font-semibold leading-snug">
                        <span
                          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                            navy ? "bg-collect-gold" : "bg-collect-red"
                          }`}
                        />
                        <span className={navy ? "text-white/85" : "text-navy/80"}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </SectionShell>
    </>
  );
}

export default CollectStory;
